import React, { useState, useEffect, useRef } from "react";
import {
    Avatar,
    Box,
    Card,
    Fade,
    Menu,
    MenuItem,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Button,
    LinearProgress,
} from "@mui/material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import { getLeaveCountsSummary } from "../../../services/leaveManagement.service";
import { encryptData } from "../../../utils/cryptoUtils";

const LeaveCountsSummary = ({ renderFile }) => {
    const [leaveData, setLeaveData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [showContent, setShowContent] = useState(false);

    // Filter states
    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedMonth, setSelectedMonth] = useState(""); // Empty means all months

    // Menu anchor states
    const [yearAnchorEl, setYearAnchorEl] = useState(null);
    const [monthAnchorEl, setMonthAnchorEl] = useState(null);
    const isYearMenuOpen = Boolean(yearAnchorEl);
    const isMonthMenuOpen = Boolean(monthAnchorEl);

    // Abort controller for canceling requests
    const abortControllerRef = useRef(null);

    // Generate year options (last 5 years + current + next year)
    const yearOptions = [];
    for (let i = currentYear - 5; i <= currentYear + 1; i++) {
        yearOptions.push(i);
    }

    // Month options
    const monthOptions = [
        { value: "", label: "All Months" },
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" },
    ];

    // Leave type columns configuration
    const leaveColumns = [
        { key: 'plannedLeave', label: 'Planned', color: '#1E40AF', bgColor: '#DBEAFE', emptyBg: '#F3F4F6' },
        { key: 'unplannedLeave', label: 'Unplanned', color: '#92400E', bgColor: '#FEF3C7', emptyBg: '#F3F4F6' },
        { key: 'lop', label: 'LOP', color: '#991B1B', bgColor: '#FEE2E2', emptyBg: '#F3F4F6' },
        { key: 'earnedLeave', label: 'Earned', color: '#065F46', bgColor: '#D1FAE5', emptyBg: '#F3F4F6' },
    ];

    useEffect(() => {
        const fetchLeaveCountsSummary = async () => {
            // Cancel any ongoing request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();

            // Only show loading for initial load, otherwise keep previous data visible
            if (isInitialLoad) {
                setIsLoading(true);
                setShowContent(false);
            }

            try {
                const params = {};
                if (selectedYear) {
                    params.year = encryptData(selectedYear.toString());
                }
                if (selectedMonth) {
                    params.month = encryptData(selectedMonth.toString());
                }

                const response = await getLeaveCountsSummary(params);

                if (response?.data?.data) {
                    // Fade out current content
                    setShowContent(false);

                    // Small delay for smooth transition
                    await new Promise(resolve => setTimeout(resolve, 150));

                    setLeaveData(response.data.data);

                    // Fade in new content
                    setTimeout(() => {
                        setShowContent(true);
                    }, 50);
                } else {
                    setShowContent(false);
                    await new Promise(resolve => setTimeout(resolve, 150));
                    setLeaveData([]);
                    setTimeout(() => {
                        setShowContent(true);
                    }, 50);
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error("Error fetching leave counts summary:", error);
                    setShowContent(false);
                    await new Promise(resolve => setTimeout(resolve, 150));
                    setLeaveData([]);
                    setTimeout(() => {
                        setShowContent(true);
                    }, 50);
                }
            } finally {
                setIsLoading(false);
                setIsInitialLoad(false);
            }
        };

        fetchLeaveCountsSummary();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [selectedYear, selectedMonth]);

    const handleYearMenuClick = (event) => {
        setYearAnchorEl(event.currentTarget);
    };

    const handleYearMenuClose = () => {
        setYearAnchorEl(null);
    };

    const handleYearSelect = (year) => {
        setSelectedYear(year);
        handleYearMenuClose();
    };

    const handleMonthMenuClick = (event) => {
        setMonthAnchorEl(event.currentTarget);
    };

    const handleMonthMenuClose = () => {
        setMonthAnchorEl(null);
    };

    const handleMonthSelect = (month) => {
        setSelectedMonth(month);
        handleMonthMenuClose();
    };

    const getMonthLabel = (monthValue) => {
        const month = monthOptions.find((m) => m.value === monthValue);
        return month ? month.label : "All Months";
    };

    const renderUserAvatar = (userFile) => {
        if (renderFile) {
            return renderFile(userFile);
        }
        if (userFile) {
            return (
                <img
                    src={userFile}
                    alt="User"
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                    }}
                />
            );
        }
        return <NoPhotographyIcon sx={{ color: "#9CA3AF" }} />;
    };

    // Render skeleton rows
    const renderSkeletonRows = () => (
        Array.from(new Array(5)).map((_, index) => (
            <TableRow key={index}>
                <TableCell sx={{ pl: 3, py: 2, borderBottom: "1px solid #F3F4F6" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Skeleton variant="circular" width={36} height={36} animation="wave" />
                        <Skeleton
                            variant="rounded"
                            width={index % 2 === 0 ? 120 : 100}
                            height={16}
                            animation="wave"
                            sx={{ borderRadius: "6px" }}
                        />
                    </Box>
                </TableCell>
                {leaveColumns.map((col) => (
                    <TableCell key={col.key} align="center" sx={{ py: 2, borderBottom: "1px solid #F3F4F6" }}>
                        <Skeleton
                            variant="rounded"
                            width={40}
                            height={24}
                            animation="wave"
                            sx={{ borderRadius: "12px", mx: "auto" }}
                        />
                    </TableCell>
                ))}
                <TableCell align="center" sx={{ pr: 3, py: 2, borderBottom: "1px solid #F3F4F6" }}>
                    <Skeleton
                        variant="rounded"
                        width={45}
                        height={26}
                        animation="wave"
                        sx={{ borderRadius: "12px", mx: "auto" }}
                    />
                </TableCell>
            </TableRow>
        ))
    );

    // Render data rows with animations
    const renderDataRows = () => (
        leaveData.map((employee, rowIndex) => (
            <TableRow
                key={employee.userId || rowIndex}
                hover
                sx={{
                    transition: "all 0.3s ease",
                    animation: "rowFadeIn 0.4s ease-out",
                    animationDelay: `${rowIndex * 0.06}s`,
                    animationFillMode: "backwards",
                    "@keyframes rowFadeIn": {
                        "0%": {
                            opacity: 0,
                            transform: "translateX(-15px)",
                        },
                        "100%": {
                            opacity: 1,
                            transform: "translateX(0)",
                        },
                    },
                    "&:hover": {
                        backgroundColor: "#F8FAFC",
                        transform: "scale(1.002)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    },
                    "&:last-child td": {
                        borderBottom: "none",
                    },
                }}
            >
                <TableCell sx={{ py: 1.5, pl: 3, borderBottom: "1px solid #F3F4F6" }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar
                            sx={{
                                width: 36,
                                height: 36,
                                border: "2px solid #E5E7EB",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                transition: "transform 0.2s ease",
                                "&:hover": {
                                    transform: "scale(1.1)",
                                },
                            }}
                        >
                            {renderUserAvatar(employee.userFile)}
                        </Avatar>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "13px",
                                color: "#111827",
                            }}
                        >
                            {employee.userName || "Unknown"}
                        </Typography>
                    </Stack>
                </TableCell>

                {leaveColumns.map((col, colIndex) => {
                    const value = employee[col.key] || 0;
                    return (
                        <TableCell
                            key={col.key}
                            align="center"
                            sx={{
                                py: 1.5,
                                borderBottom: "1px solid #F3F4F6",
                                animation: "cellPop 0.3s ease-out",
                                animationDelay: `${(rowIndex * 0.06) + (colIndex * 0.04)}s`,
                                animationFillMode: "backwards",
                                "@keyframes cellPop": {
                                    "0%": {
                                        opacity: 0,
                                        transform: "scale(0.8)",
                                    },
                                    "100%": {
                                        opacity: 1,
                                        transform: "scale(1)",
                                    },
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: value > 0 ? col.bgColor : col.emptyBg,
                                    color: value > 0 ? col.color : "#9CA3AF",
                                    px: 1.5,
                                    py: 0.4,
                                    borderRadius: "12px",
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    minWidth: "36px",
                                    transition: "all 0.2s ease",
                                    cursor: "default",
                                    "&:hover": {
                                        transform: value > 0 ? "scale(1.15)" : "none",
                                        boxShadow: value > 0 ? `0 4px 12px ${col.bgColor}` : "none",
                                    },
                                }}
                            >
                                {value}
                            </Box>
                        </TableCell>
                    );
                })}

                <TableCell
                    align="center"
                    sx={{ py: 1.5, pr: 3, borderBottom: "1px solid #F3F4F6" }}
                >
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
                            color: "#4F46E5",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "12px",
                            fontSize: "13px",
                            fontWeight: 800,
                            minWidth: "40px",
                            boxShadow: "0 2px 8px rgba(79, 70, 229, 0.15)",
                            transition: "all 0.2s ease",
                            "&:hover": {
                                transform: "scale(1.1)",
                                boxShadow: "0 4px 16px rgba(79, 70, 229, 0.25)",
                            },
                        }}
                    >
                        {employee.totalLeaves || 0}
                    </Box>
                </TableCell>
            </TableRow>
        ))
    );

    // Render empty state
    const renderEmptyState = () => (
        <TableRow>
            <TableCell colSpan={6} align="center" sx={{ py: 6, border: "none" }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Box
                        sx={{
                            width: 60,
                            height: 60,
                            borderRadius: "50%",
                            bgcolor: "#F3F4F6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                        }}
                    >
                        <Typography sx={{ fontSize: "24px" }}>📋</Typography>
                    </Box>
                    <Typography
                        sx={{ fontWeight: 700, fontSize: "16px", color: "#111827", mb: 0.5 }}
                    >
                        No Approved Leaves
                    </Typography>
                    <Typography
                        sx={{
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "#6B7280",
                            textAlign: "center",
                            maxWidth: "300px",
                        }}
                    >
                        No approved leave records found for {getMonthLabel(selectedMonth)} {selectedYear}.
                    </Typography>
                </Box>
            </TableCell>
        </TableRow>
    );

    return (
        <Card
            sx={{
                borderRadius: "16px",
                boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.05)",
                border: "1px solid rgba(0,0,0,0.02)",
                mt: 2,
                position: "relative",
            }}
        >
            {/* Loading Progress Bar - Only show when updating, not initial load */}
            {isLoading && !isInitialLoad && (
                <LinearProgress
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 2,
                        zIndex: 1001,
                        "& .MuiLinearProgress-bar": {
                            background: "linear-gradient(90deg, #4F46E5, #7C3AED)",
                        },
                    }}
                />
            )}

            {/* Header with Filters */}
            <Box
                sx={{
                    p: 2.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid #F0F0F0",
                    bgcolor: "#fff",
                    flexWrap: "wrap",
                    gap: 1
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: "16px",
                            color: "#111827",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        Leave Summary (Approved)
                    </Typography>
                    <Typography sx={{ color: "#6B7280", fontSize: "12px", mt: 0.2 }}>
                        Employee leave counts by type
                    </Typography>
                </Box>

                {/* Filters */}
                <Stack direction="row" spacing={1} alignItems="center">
                    {/* Year Filter Button */}
                    <Button
                        id="year-filter-button"
                        aria-controls={isYearMenuOpen ? "year-filter-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={isYearMenuOpen ? "true" : undefined}
                        onClick={handleYearMenuClick}
                        disabled={isLoading && isInitialLoad}
                        endIcon={<KeyboardArrowDownOutlinedIcon sx={{ fontSize: "18px" }} />}
                        sx={{
                            color: "#4F46E5",
                            fontSize: "13px",
                            textTransform: "capitalize",
                            fontWeight: 700,
                            bgcolor: "#EEF2FF",
                            borderRadius: "8px",
                            px: 1.5,
                            py: 0.5,
                            minWidth: "auto",
                            transition: "all 0.2s ease",
                            "&:hover": { bgcolor: "#E0E7FF" },
                            "&:disabled": { opacity: 0.6 },
                        }}
                    >
                        {selectedYear}
                    </Button>
                    <Menu
                        id="year-filter-menu"
                        anchorEl={yearAnchorEl}
                        open={isYearMenuOpen}
                        onClose={handleYearMenuClose}
                        MenuListProps={{
                            "aria-labelledby": "year-filter-button",
                        }}
                        TransitionProps={{ timeout: 200 }}
                        PaperProps={{
                            sx: {
                                borderRadius: "12px",
                                mt: 1,
                                boxShadow: "0px 10px 40px rgba(0,0,0,0.12)",
                                border: "1px solid #f0f0f0",
                                maxHeight: "250px",
                            },
                        }}
                    >
                        {yearOptions.map((year) => (
                            <MenuItem
                                key={year}
                                onClick={() => handleYearSelect(year)}
                                selected={year === selectedYear}
                                sx={{
                                    fontSize: "13px",
                                    fontWeight: year === selectedYear ? 700 : 500,
                                    color: year === selectedYear ? "#4F46E5" : "#374151",
                                    bgcolor: year === selectedYear ? "#EEF2FF" : "transparent",
                                    transition: "all 0.15s ease",
                                    "&:hover": {
                                        bgcolor: "#F3F4F6",
                                    },
                                }}
                            >
                                {year}
                            </MenuItem>
                        ))}
                    </Menu>

                    {/* Month Filter Button */}
                    <Button
                        id="month-filter-button"
                        aria-controls={isMonthMenuOpen ? "month-filter-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={isMonthMenuOpen ? "true" : undefined}
                        onClick={handleMonthMenuClick}
                        disabled={isLoading && isInitialLoad}
                        endIcon={<KeyboardArrowDownOutlinedIcon sx={{ fontSize: "18px" }} />}
                        sx={{
                            color: "#059669",
                            fontSize: "13px",
                            textTransform: "capitalize",
                            fontWeight: 700,
                            bgcolor: "#ECFDF5",
                            borderRadius: "8px",
                            px: 1.5,
                            py: 0.5,
                            minWidth: "auto",
                            transition: "all 0.2s ease",
                            "&:hover": { bgcolor: "#D1FAE5" },
                            "&:disabled": { opacity: 0.6 },
                        }}
                    >
                        {getMonthLabel(selectedMonth)}
                    </Button>
                    <Menu
                        id="month-filter-menu"
                        anchorEl={monthAnchorEl}
                        open={isMonthMenuOpen}
                        onClose={handleMonthMenuClose}
                        MenuListProps={{
                            "aria-labelledby": "month-filter-button",
                        }}
                        TransitionProps={{ timeout: 200 }}
                        PaperProps={{
                            sx: {
                                borderRadius: "12px",
                                mt: 1,
                                boxShadow: "0px 10px 40px rgba(0,0,0,0.12)",
                                border: "1px solid #f0f0f0",
                                maxHeight: "300px",
                            },
                        }}
                    >
                        {monthOptions.map((month) => (
                            <MenuItem
                                key={month.value}
                                onClick={() => handleMonthSelect(month.value)}
                                selected={month.value === selectedMonth}
                                sx={{
                                    fontSize: "13px",
                                    fontWeight: month.value === selectedMonth ? 700 : 500,
                                    color: month.value === selectedMonth ? "#059669" : "#374151",
                                    bgcolor: month.value === selectedMonth ? "#ECFDF5" : "transparent",
                                    transition: "all 0.15s ease",
                                    "&:hover": {
                                        bgcolor: "#F3F4F6",
                                    },
                                }}
                            >
                                {month.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </Stack>
            </Box>

            {/* Table Content */}
            <TableContainer
                sx={{
                    flexGrow: 1,
                    maxHeight: "300px",
                    overflowX: "auto",
                    width: "100%",
                    WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
                    "&::-webkit-scrollbar": {
                        height: "6px",
                        width: "6px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        background: "#cbd5e1",
                        borderRadius: "3px",
                    },
                }}
            >
                <Table stickyHeader size="small" sx={{ minWidth: "650px" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    backgroundColor: "#F9FAFB",
                                    color: "#4B5563",
                                    fontWeight: 700,
                                    fontSize: "12px",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                    py: 1.5,
                                    pl: 3,
                                    borderBottom: "2px solid #E5E7EB",
                                    zIndex: 1000,
                                }}
                            >
                                Employee
                            </TableCell>
                            {leaveColumns.map((col) => (
                                <TableCell
                                    key={col.key}
                                    align="center"
                                    sx={{
                                        backgroundColor: "#F9FAFB",
                                        color: col.color,
                                        fontWeight: 700,
                                        fontSize: "12px",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                        py: 1.5,
                                        borderBottom: "2px solid #E5E7EB",
                                        zIndex: 1000,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {col.label}
                                </TableCell>
                            ))}
                            <TableCell
                                align="center"
                                sx={{
                                    backgroundColor: "#F9FAFB",
                                    color: "#4F46E5",
                                    fontWeight: 700,
                                    fontSize: "12px",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                    py: 1.5,
                                    pr: 3,
                                    borderBottom: "2px solid #E5E7EB",
                                    zIndex: 1000,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Total
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading && isInitialLoad ? (
                            renderSkeletonRows()
                        ) : (
                            <Fade in={showContent} timeout={300}>
                                <TableBody component="div" sx={{ display: "contents" }}>
                                    {leaveData.length > 0 ? renderDataRows() : renderEmptyState()}
                                </TableBody>
                            </Fade>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    );
};

export default LeaveCountsSummary;
